/**
 * Chinese UI copy — the source dictionary. `en.ts` must mirror this shape
 * exactly (enforced via `satisfies typeof zh`), so adding a key here without
 * translating it is a compile error.
 */
export const zh = {
	app: {
		title: "Muhaha · 游戏与短剧",
	},
	meta: {
		pageTitle: "{{page}} · Muhaha",
		searchTitle: "搜索",
		searchDescription: "在 Muhaha 搜索游戏、短剧和创作者。",
		playableDescription: "在 Muhaha 玩社区小游戏、看互动短剧。",
		profileDescription: "{{name}} 在 Muhaha 发布的游戏与短剧。",
		topicDescription: "Muhaha 上带 #{{topic}} 标签的游戏与短剧。",
		termsDescription: "使用 Muhaha 时你同意的服务条款。",
		privacyDescription: "Muhaha 如何收集、使用和保护你的数据。",
	},
	notFound: {
		title: "页面不存在",
		description: "链接可能已失效，或内容已被删除。",
		backHome: "回到发现页",
	},
	common: {
		back: "返回",
		loading: "加载中…",
		loadFailed: "加载失败",
		loadMore: "加载更多",
		loadMoreFailed: "加载更多失败",
		refresh: "刷新",
		retry: "重试",
		cancel: "取消",
		playAd: "播放广告",
		cancelAd: "取消广告",
		loadingAd: "广告加载中",
		advertisement: "广告",
		remove: "移除",
		confirm: "确认",
		close: "关闭",
		send: "发送",
		signIn: "登录",
		signInUp: "登录 / 注册",
		justNow: "刚刚",
	},
	datePicker: {
		placeholder: "选择日期",
		clear: "清除日期",
	},
	layout: {
		search: "搜索 Muhaha",
		home: "Muhaha 首页",
		about: "关于",
		playOnMobile: "手机畅玩",
		notifications: "通知",
		myProfile: "我的主页",
		accountMenu: "账户菜单",
		community: "社区",
		lightMode: "浅色模式",
		darkMode: "深色模式",
		settings: "设置",
		signOut: "退出登录",
		guestMode: "游客模式",
		popularTopics: "热门话题",
		allTopics: "全部话题",
		noTopics: "暂时没有热门话题",
	},
	mobilePlay: {
		getTheApp: "下载 App",
		scanToPlay: "扫码，在手机上畅玩",
		title: "在手机上玩",
		description: "用手机扫描二维码，下载 Muhaha App。",
		downloadTitle: "下载 Muhaha App",
		downloadDescription: "选择应用商店，下载 iOS 或 Android 版。",
		storeDownloadDescription: "使用手机扫描二维码，或选择应用商店下载 Muhaha。",
		redirectingDescription: "正在前往适合你设备的应用商店；如果没有自动跳转，请在下方选择。",
		iosStore: "iOS · App Store",
		androidStore: "Android · Google Play",
		iosDownload: "下载 iOS 版",
		androidDownload: "下载 Android 版",
		downloadQrAlt: "下载 Muhaha App 的二维码",
		showQr: "查看二维码",
		copyLink: "复制链接",
		linkCopied: "链接已复制",
		copyFailed: "无法复制链接",
		qrAlt: "在手机上打开 Muhaha 的二维码",
	},
	about: {
		eyebrow: "关于 Muhaha",
		title: "小游戏与互动短剧",
		description:
			"Muhaha 是一个汇聚海量迷你互动内容的创作平台，从小游戏到互动短剧，应有尽有。无论网页还是手机，你都可以随时畅玩、创作，并把喜欢的内容变成自己的版本。",
		mobileTitleLead: "发现",
		mobileTitleAccent: "无限好玩。",
		mobileDownloadCta: "扫码在手机上玩 Muhaha",
		createTitle: "创作一切",
		createText: "把一个想法变成可玩的游戏或有分支的互动短剧。",
		playTitle: "一起玩",
		playText: "探索社区创作的世界，并把此刻分享给朋友。",
		remixTitle: "快速 Remix",
		remixText: "借用已有结构，换一种风格，再发布属于你的版本。",
		tagline: "创作、畅玩、再创作。",
	},
	footer: {
		copyright: "版权所有 © 2026 Yahaha Studios。保留所有权利。",
		navigation: "页脚导航",
		support: "支持",
		privacy: "隐私政策",
		terms: "服务条款",
	},
	nav: {
		discover: "探索",
		create: "创作",
		library: "书架",
		friends: "好友",
		messages: "消息",
		wallet: "积分",
		me: "我",
	},
	contentType: {
		all: "全部",
		game: "游戏",
		drama: "短剧",
	},
	card: {
		play: "开始玩",
		watch: "观看",
		like: "点赞",
	},
	auth: {
		modalTitle: "登录 Muhaha",
		signInTitle: "登录",
		signUpTitle: "创建账号",
		newToMuhaha: "还没有 Muhaha 账号？",
		haveAccount: "已经有账号？",
		signUp: "注册",
		heroTitle: "开启你的互动世界",
		heroSubtitle: "用邮箱验证码登录，开启完整体验",
		emailLabel: "邮箱地址",
		changeEmail: "换个邮箱",
		codeSentTo: "已发送验证码至",
		codePlaceholder: "6 位验证码",
		resendIn: "{{s}}s 后可重发",
		resend: "重新发送验证码",
		getCode: "获取验证码",
		continueWithEmail: "使用邮箱继续",
		verifyAndContinue: "验证并继续",
		signIn: "登录",
		or: "或",
		googleContinue: "通过 Google 继续",
		appleContinue: "通过 Apple 继续",
		appleNoToken: "Apple 未返回身份令牌",
		guestContinue: "先体验一下（游客）",
		agreeTos: "登录即表示同意 Muhaha 的服务条款与隐私政策",
		agreePrefix: "登录即表示同意 Muhaha 的",
		termsOfService: "服务条款",
		agreeJoiner: "与",
		privacyPolicy: "隐私政策",
		gateTitle: "登录后继续",
		gateDesc: "登录 Muhaha 即可开启对话、创作与收藏。",
		sessionExpired: "登录状态已过期",
		inviteBannerText: "有人邀请你加入 Muhaha，邀请码",
		inviteBannerCta: "注册关联",
		inviteCodeToggle: "有邀请码？",
		inviteCodeLabel: "邀请码",
		inviteCodePlaceholder: "6 位邀请码",
		inviteCodeApplied: "将使用邀请码注册",
		inviteCodeChange: "修改",
		inviteCodeValid: "格式正确，注册时会一并提交",
		inviteCodeInvalid: "邀请码为 6 位字符，不含 I、O、0、1",
		codeSent: "验证码已发送至邮箱",
		codeSendFailed: "验证码发送失败",
		signedIn: "登录成功",
		signedInWithInvite: "登录成功，邀请码 {{code}} 已随注册提交",
		codeInvalid: "验证码无效",
		googleFailed: "Google 登录失败",
		appleFailed: "Apple 登录失败",
		guestEntered: "已进入体验模式",
		guestFailed: "体验模式进入失败",
		signInFailed: "登录失败",
		traveler: "旅人",
	},
	login: {
		redirecting: "正在跳转…",
		authorizeFailed: "登录未能完成，请重试。",
	},
	loginSupport: {
		title: "无法登录？",
		subtitle: "按照以下步骤找回你的 Muhaha 账号。",
		emailSubject: "Muhaha 登录帮助",
		emailSignInMethod: "登录方式：",
		emailDevice: "设备和操作系统：",
		emailAppVersion: "App 版本：",
		emailProblem: "问题描述：",
		sameMethodTitle: "使用相同的登录方式",
		sameMethodBody1: "如果你已有 Muhaha 账号，请选择注册账号时使用的相同登录方式。",
		sameMethodBody2:
			"在登录页面选择“通过 Apple 继续”、“通过 Google 继续”，或输入邮箱地址接收验证码。",
		appleTitle: "Apple 登录",
		appleBody1: "请确认已登录正确的 Apple 账户，然后完成 Apple 授权窗口中的操作，不要中途关闭。",
		appleBody2: "如果授权窗口被取消或关闭，请返回 Muhaha，再次点击同一按钮。",
		googleTitle: "Google 登录",
		googleBody1: "请确认设备上已登录正确的 Google 账号，然后完成登录窗口中的操作，不要中途关闭。",
		googleBody2: "如果登录窗口被取消或关闭，请返回 Muhaha，再次点击同一按钮。",
		emailCodeTitle: "邮箱验证码",
		emailCodeBody1: "请确认邮箱地址正确，然后在收件箱、垃圾邮件或广告邮件中查找 Muhaha 验证码。",
		emailCodeBody2: "如果申请过多个验证码，请使用最新的一个。你也可以在邮箱登录页面重新获取验证码。",
		retryTitle: "重试之前",
		retryBody1: "请检查网络连接，完全关闭并重新打开 Muhaha，并安装最新版本后再试。",
		retryBody2: "如果登录受阻，请尝试其他网络，并暂时关闭 VPN 或代理。",
		helpTitle: "仍需帮助？",
		helpBody:
			"请发送邮件至 [{{email}}](<{{href}}>)，并附上登录方式、设备和操作系统、App 版本、问题发生的大致时间以及错误截图。",
		securityBody: "为了账号安全，请勿向我们发送邮箱验证码。",
	},
	playable: {
		untitled: "未命名作品",
		defaultTagline: "一个等待被开启的作品",
		anonymousAuthor: "匿名创作者",
		expired: "已过期下架",
		expiresInMinutes: "{{count}} 分钟后下架",
		expiresInHours: "{{count}} 小时后下架",
		expiresInDays: "{{count}} 天后下架",
		pinned: "置顶",
		draft: "草稿",
		archived: "已下架",
		anonPublished: "匿名发布",
		unpublishedEdits: "有未发布的修改",
		visibilityFollowers: "粉丝可见",
		visibilityFriends: "好友可见",
		visibilityUnlisted: "仅限链接",
		visibilityPrivate: "仅自己可见",
		visibilityDirected: "定向赠送",
	},
	media: {
		zoom: "缩放",
		horizontal: "水平位置",
		vertical: "垂直位置",
		applyCrop: "应用裁剪",
		cropFailed: "无法读取或裁剪这张图片",
	},
		comments: {
			playableAttachment: "可玩二创",
			attachMedia: "添加图片或视频",
			invalidMedia: "请选择不超过 80 MB 的图片或视频",
			reply: "回复",
			replyPlaceholder: "写下回复……",
			showReplies: "查看 {{count}} 条回复",
			hideReplies: "收起回复",
			loadMoreReplies: "加载更多回复",
			likeFailed: "点赞失败",
			replyFailed: "回复发送失败",
			repliesLoadFailed: "回复加载失败",
		},
	createGame: {
		quick: "Quick",
		deep: "Deep",
		quickHint: "快速生成",
		deepHint: "深度生成",
		remixMode: "Remix 模式",
		remixOf: "正在二创",
		remixOfHint: "你的提示词会叠加在这个作品之上。",
		remixSourceUnavailable: "这个作品加载失败。",
		remixNotAllowed: "作者没有向你开放这个作品的二创。",
		remixUnsupported: "这个游戏暂不支持二创。",
		remixUntitled: "未命名作品",
		remixClear: "改为从零开始",
		promptPlaceholder: "描述你想做的小游戏：玩法、角色、氛围、胜负条件、想要的视觉风格……",
		generate: "生成游戏",
		freeLeft: "前三次免费生成 · 还剩 {{count}} 次",
		costHint: "每次生成消耗积分 · 当前 {{count}}",
		notEnoughCredits: "积分不足，无法开始生成。",
		startFailed: "生成启动失败",
		attachImage: "添加图片",
		attachAudio: "添加音频",
		powers: "Powers",
		hotPowers: "热门 Powers",
		selectionLimit: "最多选择 {{count}} 项",
		deepRemixUnavailable: "Remix 当前使用 Quick 模式",
		deepAttachmentsUnavailable: "Deep 规划暂不支持媒体附件",
		remixDemos: "Remix 灵感",
		remixDemosHint: "选择一个案例开始二创",
		before: "改造前",
		after: "改造后",
		remixCount: "{{count}} 次 Remix",
		attachmentsLabel: "已添加的附件",
		removeAttachment: "移除附件 {{name}}",
		attachmentLimit: "最多添加 {{count}} 个附件",
		attachmentInvalidType: "文件类型不受支持",
		attachmentTooLarge: "单个附件不能超过 10 MB",
		attachmentUploadFailed: "附件上传失败，请重试",
		powersComingSoon: "Powers 功能即将开放",
		openWallet: "去钱包",
		handoffUnavailable: "Muhaha Create 暂不可用，无法开始创作。",
		redirectingToNow: "正在打开 Muhaha Create…",
		description: "发送后会进入 Muhaha Create 画布编辑器，在那里完成生成，并继续精修。",
	},
	remix: {
		title: "Remix「{{title}}」",
		untitled: "未命名作品",
		promptTitle: "有新点子？来 Remix 吧！",
		placeholder: "输入你的创意，开始创作……",
		send: "开始 Remix",
		hint: "会先复制成你的草稿，再基于这个草稿生成新版本。",
		notEnoughCredits: "积分不足，无法 Remix。",
		prepareFailed: "Remix 准备失败，请稍后再试",
	},
	discover: {
		expandSidebar: "展开侧边栏",
		pinSidebar: "保持展开",
		collapseSidebar: "收起侧边栏",
		expandPlayNext: "展开猜你喜欢",
		collapsePlayNext: "收起猜你喜欢",

		followingDescription: "关注的创作者发布的新游戏。",
		followingEmptyTitle: "发现你喜欢的创作者",
		followingEmptyDescription: "这里还没有游戏。关注几位创作者，让喜欢的作品出现在这里。",
		creatorsToFollow: "你可能喜欢的创作者",
		creatorsToFollowDescription: "试试他们的作品，关注下一份惊喜。",
		viewFollowing: "看看他们的游戏",
		followCreator: "关注 {{name}}",
		unfollowCreator: "取消关注 {{name}}",
		creatorFollowers: "{{followers}} 位关注者",
		visitCreator: "看看创作者主页",
		noCreatorSuggestions: "暂时没有推荐创作者，去探索游戏，发现新的灵感吧。",
		curatedStarterTitle: "从一款精选游戏开始",
		exploreStarterTitle: "探索推荐",
		starterDescription: "挑一张喜欢的封面，点开就能玩。",
		noStarterSuggestions: "去探索最新游戏，找到你的第一款心头好。",

		browseGames: "浏览游戏",
		recentlyPlayed: "最近玩过",
		latest: "最新上架",
		popular: "热门游戏",
		recentlyPlayedDescription: "接着上次继续玩，最近游玩的游戏排在最前面。",
		latestDescription: "发现新上架的游戏，按发布时间从新到旧排列。",
		popularDescription: "看看大家最近都在玩什么。",
		historySignInTitle: "找回最近玩过的游戏",
		historySignInDescription: "登录后查看你的游玩历史，随时回来接着玩。",
		followingSignInTitle: "关注喜欢的创作者",
		followingSignInDescription: "登录后查看你关注的创作者发布的游戏。",
		historyEmptyTitle: "从一款喜欢的游戏开始",
		historyEmptyDescription: "玩过的游戏会出现在这里，方便你下次继续。",
		heroFeatured: "精选推荐",
		autoPreviews: "自动游戏预览",
		pausePreviews: "暂停预览",
		resumePreviews: "开启预览",
		sortGames: "游戏排序",
		sortFeatured: "精选",
		sortHot: "热门",
		sortLatest: "最新",
		allTags: "全部标签",
		allTagsDescription: "按类型、风格和玩法，找到下一款喜欢的游戏。",
		findTag: "筛选标签…",
		noTags: "没有匹配的标签",
		randomGame: "随机游戏",
		backToTop: "返回顶部",
		randomFailed: "暂时无法获取游戏，请重试。",
		playNext: "猜你喜欢",
		noSimilarGames: "更多游戏正在赶来。",
		collections: "精选栏目",
		playCount: "{{value}} 次播放",
		createdBy: "创作者",
		explore: "探索",
		exploreDescription: "发现一点新鲜，遇见下一份喜欢。",
		browseDescription: "选一个喜欢的，即刻开玩。",
		categories: "浏览分类",
		categoriesFailed: "分类暂时无法加载。",
		seeAll: "查看全部",
		backToExplore: "返回探索",
		previousIn: "{{title}}的上一组内容",
		nextIn: "{{title}}的下一组内容",
		game: "小游戏",
		drama: "互动故事",

		forYou: "推荐",
		trending: "趋势",
		following: "关注",
		signInTitle: "登录后开始探索",
		signInDesc: "登录后即可浏览社区创作的小游戏与互动短剧。",
		signInNow: "立即登录",
		loadingContent: "正在加载内容…",
		emptyTitle: "暂时没有内容",
		emptyDesc: "社区内容正在赶来的路上，稍后再刷新试试。",
		loadFailed: "内容加载失败",
		heroDesc:
			"和社区一起创作可玩小游戏与互动短剧。打开即玩，喜欢就改，灵感来了马上发布。",
		heroCreate: "创作。",
		heroPlay: "畅玩。",
		heroRemix: "再创作。",
		tileDramas: "短剧宇宙",
		tileStories: "互动剧情",
		tileScripts: "共创剧本",
		tileRadio: "灵感电台",
	},
	friends: {
		eyebrow: "好友",
		title: "好友",
		signInTitle: "登录后查看好友",
		signInDesc: "添加好友、处理好友请求，并从可玩内容里选择好友互动。",
		pageDesc: "管理好友和请求。",
		addFriend: "添加好友",
		friendsTab: "好友",
		requestsTab: "请求",
		newRequests: "有新的好友请求",
		noFriends: "还没有好友",
		noFriendsDesc: "通过用户名添加好友，或在作品里发起好友互动。",
		dmButton: "发私信",
		removeButton: "移除好友",
		removeConfirm: "确定要移除好友 {{name}} 吗？",
		removedNotice: "好友已移除",
		removeFailed: "移除好友失败",
		blockedTab: "已屏蔽",
		blockButton: "屏蔽",
		blockConfirm: "确定要屏蔽 {{name}} 吗？对方将无法给你发私信或好友请求，你们也会解除好友关系。",
		blockedNotice: "已屏蔽该用户",
		blockFailed: "屏蔽用户失败",
		unblockButton: "解除屏蔽",
		unblockedNotice: "已解除屏蔽",
		unblockFailed: "解除屏蔽失败",
		noBlocked: "没有已屏蔽的用户",
		noBlockedDesc: "被屏蔽的用户无法给你发私信或好友请求。",
		noRequests: "没有待处理请求",
		noRequestsDesc: "新的好友请求会显示在这里。",
		wantsToAdd: "请求添加你为好友",
		ignore: "忽略",
		accept: "接受",
		addModalDesc: "输入对方的用户名。",
		handleLabel: "用户名",
		sendRequest: "发送请求",
		loadFailed: "好友加载失败",
		handleRequired: "请输入用户名",
		nowFriends: "已成为好友",
		requestSent: "好友请求已发送",
		requestFailed: "好友请求发送失败",
		acceptedNotice: "已接受好友请求",
		acceptFailed: "接受好友请求失败",
		declinedNotice: "已忽略好友请求",
		declineFailed: "处理好友请求失败",
		bridgeRequestDesc: "这个作品想向该用户发送好友请求。",
		pickTitle: "选择好友",
		pickedCount: "已选择 {{picked}}/{{limit}}",
		noFriendsYet: "还没有好友。",
		goAdd: "去添加",
		unknownUser: "未知用户",
		user: "用户",
	},
	dm: {
		title: "私信",
		signInTitle: "登录后查看私信",
		signInDesc: "和已接受的好友进行 Stream Chat 私信。",
		pageDesc: "基于 Stream Chat，仅好友之间可发起。",
		empty: "暂无私信",
		emptyDesc: "去好友页选择一个好友开始聊天。",
		goFriends: "去好友页",
		noMessagesYet: "还没有消息",
		sayHi: "还没有消息，打个招呼吧。",
		inputPlaceholder: "输入消息…",
		resizeWindow: "调整聊天窗口大小",
		loadFailed: "私信加载失败",
		openFailed: "无法打开私信",
		messagesLoadFailed: "消息加载失败",
		messageReactions: "消息回应",
		sendFailed: "消息发送失败",
		friendRequired: "成为好友后才能聊天",
		friendRequiredDesc: "先发送好友请求，等待对方接受。",
		offline: "网络已断开，恢复后会自动重新连接。",
		sharedPlayableBy: "作者",
		sharedPlayablePlays: "{{count}} 次游玩",
		playTogether: "一起玩",
		liveRoom: "正在游戏 · {{count}} 人在房间",
		roomEnded: "该私人房间已不可用",
		joinRoom: "加入房间",
		roomUnavailable: "房间不可用",
	},
	library: {
		title: "书架",
		subtitle: "你收藏的作品与浏览足迹",
		favorites: "收藏",
		history: "历史",
		noFavorites: "还没有收藏",
		noHistory: "还没有浏览记录",
		emptyDesc: "去探索页逛逛，收藏你喜欢的作品吧。",
	},
	notifications: {
		title: "通知",
		subtitle: "谁在关注你的世界",
		read: "已读",
		empty: "暂无通知",
		emptyDesc: "有新的互动会出现在这里。",
		loadFailed: "通知加载失败",
		interacted: "与你互动",
		sections: {
			likes: "赞与收藏",
			likesDesc: "查看谁赞了或收藏了你的内容",
			following: "关注",
			followingDesc: "查看新的关注和悄悄关注",
			myPlayables: "我的作品",
			myPlayablesDesc: "你创建的内容、生成状态和场内动态",
			subscriptions: "订阅更新",
			subscriptionsDesc: "已关注创作者和收藏内容的更新",
			earlier: "最近活动",
			showAll: "全部活动",
		},
		labels: {
			follow: "关注了你",
			comment: "评论了你的作品",
			reply: "回复了你",
			likePlayable: "赞了你的作品",
			likeComment: "赞了你的评论",
			friendRequest: "请求加你为好友",
			friendAccept: "接受了你的好友请求",
			friendMatch: "和你成为了好友",
			directMessage: "发来了私信",
			admirer: "悄悄关注了你",
			levelUp: "升级了",
			playableMessage: "在作品里发来了消息",
			generationStarted: "的生成已开始",
			generationReady: "的生成已完成",
			generationFailed: "的生成失败了",
			creatorBroadcast: "发布了创作者广播",
			dramaUpdate: "更新了短剧",
			playableNew: "发布了新作品",
		},
	},
	messages: {
		markAllRead: "全部已读",
		title: "消息",
		subtitle: "对话、互动和关注动态集中在这里",
		interactions: "互动",
		newFollowers: "新关注",
		updates: "动态",
		followBack: "回关",
		conversations: "对话",
		empty: "还没有对话",
		emptyDesc: "找到想聊的人，开始第一条消息。",
		findPeople: "找人聊天",
		newGroup: "新建群聊",
		groupName: "群聊名称（可选）",
		createGroup: "创建群聊",
		groupMembers: "{{count}} 位成员",
		groupMembersTitle: "群成员",
		viewGroupMembers: "查看群成员",
		groupCreateFailed: "群聊创建失败",
		followedBack: "已回关",
		followBackFailed: "回关失败",
		addFriend: "添加好友",
		requested: "请求已发送",
		sayHi: "打招呼",
	},
	profile: {
		historyButton: "记录",
		shareProfile: "分享个人主页",
		shareProfileTitle: "分享个人主页",
		profileLink: "个人主页链接",
		copyProfileLink: "复制链接",
		accountOverview: "账号",
		viewCreditsHistory: "查看积分与交易记录",
		communityLinks: "社区",
		loadFailed: "用户加载失败",
		notFound: "用户走丢了",
		followers: "粉丝",
		followingLabel: "关注",
		postsLabel: "已发布",
		playsLabel: "播放",
		likesReceivedLabel: "作品获赞",
		favoritesLabel: "作品获收藏",
		likesAndSaves: "获赞与收藏",
		likesAndSavesTitle: "获赞与收藏",
		totalWorksPlaysLabel: "作品累计游玩",
		followersTitle: "粉丝",
		followingTitle: "正在关注",
		followListFailed: "关注列表加载失败",
		noFollowUsers: "暂无用户",
		editProfile: "编辑资料",
		myProjects: "我的项目",
		addFriend: "添加好友",
		acceptFriend: "接受好友",
		friendRequestSent: "请求已发送",
		friendsStatus: "已是好友",
		followingBtn: "已关注",
		follow: "关注",
		contentTabs: "个人作品",
		postsTab: "已发布",
		likesTab: "喜欢",
		savedTab: "已收藏",
		theirPosts: "TA 发布的作品",
		signInToViewTitle: "登录访问主页",
		signInToViewDesc: "登录后即可查看 TA 发布的游戏和短剧。",
		noPublicWorks: "还没有已发布的作品",
		createFirstWorkDesc: "从一个想法开始，创作你的第一个作品吧。",
		notPublishedWorkDesc: "TA 还没有发布作品。",
		createWork: "开始创作",
		published: "已发布",
		editWork: "编辑作品",
		editWorkFailed: "打开 Muhaha Create 失败",
		deleteWork: "删除作品",
		deleteWorkTitle: "删除作品",
		deleteWorkConfirm: "确定删除《{{title}}》吗？",
		deleteWorkHint: "作品及其互动数据将被永久删除，此操作无法撤销。",
		deletingWork: "删除中…",
		workDeleted: "作品已删除",
		deleteWorkFailed: "作品删除失败",
		workActions: "{{title}} 的操作",
		pinWork: "置顶作品",
		unpinWork: "取消置顶",
		workPinned: "作品已置顶",
		workUnpinned: "已取消置顶",
		pinWorkFailed: "置顶操作失败",
		removeLike: "取消点赞",
		likeRemoved: "已从喜欢中移除",
		likeUpdateFailed: "点赞状态更新失败",
		removeFavorite: "取消收藏",
		shareFailed: "作品分享失败",
		likesLoadFailed: "喜欢的作品加载失败",
		noLikedWorks: "还没有喜欢的作品",
			noLikedWorksDesc: "去探索感兴趣的作品吧。",
			youMayAlsoLike: "你可能也喜欢",
		message: "发消息",
		broadcastTitle: "向关注者广播",
		broadcast: "广播",
		broadcastPlaceholder: "告诉关注者这个作品有什么新内容……",
		sendBroadcast: "发送广播",
		broadcastSent: "广播已发送给 {{count}} 人",
		broadcastFailed: "广播发送失败",
		followFailed: "关注状态更新失败",
	},
	search: {
		signInToSearch: "登录后搜索游戏和创作者",
		placeholder: "搜索作品、创作者…",
		clear: "清空搜索",
		promptTitle: "搜点什么吧",
		promptDesc: "按作品名、标签或创作者昵称查找。",
		noResults: "没有找到结果",
		tryAnother: "换个关键词试试「{{query}}」",
		creators: "创作者",
		playables: "作品",
	},
	ageGate: {
		title: "暂时无法使用 Muhaha",
		description: "使用 Muhaha 需要年满 {{years}} 周岁，这是服务条款的要求。",
		mistakeHint: "日期填错了？在下面改成正确的出生日期，账号会立即解除限制。",
		birthdayLabel: "出生日期",
		required: "请填写出生日期",
		tooYoung: "需要年满 {{years}} 周岁，请检查年份后重试。",
		save: "保存并解除限制",
		saveFailed: "出生日期更新失败",
		unlocked: "出生日期已更新，欢迎回来",
		signOut: "改为退出登录",
	},
	settings: {
		title: "设置",
		subtitle: "管理你的个人资料与账号",
		avatar: "头像",
		uploadAvatar: "上传头像",
		avatarUploading: "上传中",
		avatarUploadHint: "支持图片文件，最大 5MB。上传后记得保存资料。",
		avatarUpdated: "头像已更新",
		avatarUpdateFailed: "头像更新失败",
		avatarInvalidType: "请选择图片文件",
		avatarTooLarge: "头像不能超过 5MB",
		displayName: "昵称",
		handleLabel: "用户名（handle）",
		handleHint: "仅限字母、数字和下划线",
		bio: "个人简介",
		save: "保存",
		signOut: "退出登录",
		accountType: "账号类型：",
		guest: "游客",
		registered: "注册用户",
		emailLabel: "邮箱：",
		guestNote: "游客数据仅保存在当前设备。登录邮箱以永久保存你的作品与收藏。",
		profileUpdated: "资料已更新",
		saveFailed: "保存失败",
		language: "语言",
		gender: "性别",
		preferNotToSay: "不透露",
		genderFemale: "女性",
		genderMale: "男性",
		genderNonBinary: "非二元性别",
		genderSelfDescribed: "自我描述",
		birthday: "生日",
		birthdayTooYoung: "账号持有人需年满 {{years}} 周岁",
		cropAvatar: "裁剪头像",
		notificationPrefs: "通知偏好",
		notificationPrefsFailed: "通知偏好保存失败",
		notificationGroups: {
			social: "社交关系",
			engagement: "赞与评论",
			creation: "生成进度",
			updates: "创作者与作品更新",
			messages: "作品内消息",
		},
		notificationGroupDescriptions: {
			social: "关注、好友请求和匹配",
			engagement: "作品和评论收到的新互动",
			creation: "生成开始、完成或失败",
			updates: "广播、新作品和短剧更新",
			messages: "作品内发来的消息",
		},
		accountAndPrivacy: "账户与隐私",
		cosmetics: "头像与装扮",
		downloadData: "下载我的数据",
		exportFailed: "数据导出失败",
		deleteAccount: "删除账户",
		deleteAccountWarning: "删除申请提交后，账户会进入待清除状态。请确认你已下载需要保留的数据。",
		confirmDelete: "确认删除",
		deleteScheduled: "账户将于 {{date}} 清除",
		deleteFailed: "账户删除申请失败",
	},
	gdpr: {
		accountDeletionTitle: "申请删除账户",
		accountDeletionSubtitle: "在这里提交 Muhaha 账户及相关数据的删除申请。",
		accountDeletionIntro:
			"提交后账户会立即进入待清除状态并退出登录。请先下载需要保留的数据；账户及相关数据会在清除日期永久删除。",
		requestDeletion: "申请删除账户",
		requestDeletionHint: "此操作会停用账户，且无法恢复已提交的删除申请。",
		deletionRequested: "账户将于 {{date}} 清除",
		requestFailed: "账户删除申请失败",
	},
	cosmetics: {
		title: "头像与装扮",
		subtitle: "管理已获得的头像、头像框和权益",
		preview: "当前装扮预览",
		clearFrame: "移除头像框",
		frames: "头像框",
		avatars: "头像",
		entitlements: "账户权益",
		permanent: "永久有效",
		equip: "使用",
		frameEquipped: "头像框已更换",
		avatarEquipped: "头像已更换",
		equipFailed: "装扮应用失败",
		loadFailed: "装扮加载失败",
		empty: "还没有装扮",
		emptyDesc: "获得的头像和头像框会显示在这里。",
	},
	wallet: {
		title: "积分",
		subtitle: "积分用于 AI 生成，可通过站内活动获得",
		creditsBalance: "积分余额",
		freeRemaining: "免费生成剩余 {{count}} 次",
		historyTitle: "积分记录",
		noTransactions: "还没有积分记录",
		loadFailed: "积分加载失败",
		mubuxBalance: "Mubux 余额",
		storeTitle: "购买积分",
		storeNote: "* 用 Mubux 购买，面额与价格以商店实时为准。",
		bestValue: "最划算",
		needsTopUp: "Mubux 不足，请在 Muhaha App 内充值后再购买。",
		needsTopUpWeb: "Mubux 不足，请先充值。",
		packBought: "已到账 {{amount}} 积分",
		packPending: "购买成功，积分稍后自动到账。",
		buyFailed: "购买失败",
		topupTitle: "充值 Mubux",
		topupNote: "* 通过 Stripe 安全支付，支付完成自动到账。",
		topupSuccess: "已到账 {{amount}} Mubux",
		topupProcessing: "支付成功，正在入账，稍后自动到账。",
		topupChecking: "正在确认支付结果…",
		topupFailed: "支付未完成",
		mubuxHistory: "Mubux 记录",
		noMubuxTransactions: "还没有 Mubux 交易记录",
		mubuxActivityLoadFailed: "Mubux 交易记录加载失败",
		mubuxReasons: {
			recharge: "Mubux 充值",
			received: "Mubux 入账",
			spent: "Mubux 消费",
		},
		reasons: {
			creditsRedeem: "积分入账",
			generation: "AI 生成",
			generationRefund: "生成退款",
			generationFree: "免费生成",
			generationFreeRefund: "免费次数恢复",
		},
	},
		play: {
			reload: "重新加载",
			fullscreen: "全屏",
			fullscreenFailed: "当前浏览器无法进入全屏",
		loading: "正在载入…",
		cannotLoad: "无法加载",
		noBundleUrl: "该作品缺少 bundleUrl",
		bundleLoadFailed: "资源包加载失败：HTTP {{status}}",
		playableLoadFailed: "作品加载失败",
		expiredTitle: "这个作品已经下架",
		expiredDesc: "作者把它设成了限时作品，展示时间已经结束。",
		notFoundTitle: "打不开这个作品",
		notFoundDesc: "它可能已被删除，或者作者没有把它开放给你。",
		notFoundSignIn: "登录后重试",
		ownerUnpublishedNotice: "你有未发布的修改，在 Muhaha Create 里发布最新版本。",
		sharePrivateHint: "这个作品仅自己可见，别人打不开这个链接。",
		shareScopeHint: "这个作品的可见范围是「{{scope}}」，范围外的人打不开链接。",
		openInApp: "在 App 中打开",
		getApp: "下载 App",
		prevPlayable: "上一个",
		nextPlayable: "下一个",
		creator: "创作者",
		untitledPlayable: "未命名作品",
		publishedAgo: "发布于 {{time}}",
		playsLabel: "播放",
		likesLabel: "点赞",
		commentsLabel: "评论",
		favoritesLabel: "收藏",
		like: "点赞",
		liked: "已点赞",
		likeFailed: "点赞失败",
		favorite: "收藏",
		favorited: "已收藏",
		unfavorited: "已取消收藏",
		favoriteFailed: "收藏失败",
		remix: "Remix",
		share: "分享",
		linkCopied: "链接已复制",
		shareTitle: "分享这个游戏",
		sharedBy: "{{name}} 分享给你",
		shareLink: "分享链接",
		copyLink: "复制链接",
		copyFailed: "复制失败",
		qrAlt: "游戏分享二维码",
		qrFailed: "二维码生成失败",
		commentsTitle: "评论",
		commentsLoadFailed: "评论加载失败",
		commentPlaceholder: "聊聊你的看法…",
		commentFailed: "评论发布失败",
		noComments: "还没有评论，来抢沙发～",
		deletedComment: "该评论已删除",
		purchaseTitle: "确认购买",
		purchasePrice: "{{amount}} Mubux",
		purchaseBalance: "当前余额：{{amount}} Mubux",
		purchaseInsufficient: "Mubux 不足，请先到钱包充值。",
		purchaseConfirm: "购买",
		shareToChat: "分享到聊天",
		sharedToChat: "已分享到聊天",
		shareVia: "系统分享",
	},
	rewards: {
		title: "任务与奖励",
		claim: "领取",
		claimAll: "全部领取",
		claimed: "已领取 {{count}} 积分",
		claimFailed: "奖励领取失败",
		loadFailed: "奖励加载失败",
		inviteTitle: "邀请好友",
		inviteCount: "已邀请 {{count}} 人",
		inviteCopied: "邀请链接已复制",
		inviteMessage: "好友通过你的链接注册，你们双方各得 {{count}} 金币。",
		inviteMessageBare: "邀请好友加入 Muhaha，一起玩、一起创作。",
		copyInvite: "复制邀请链接",
		shareInvite: "分享",
	},
	invite: {
		title: "邀请",
		readyTitle: "你的邀请已就绪",
		readyDesc: "注册时会自动使用这个邀请码，你和邀请人都会获得奖励。",
		signUpCta: "注册 / 登录",
		memberTitle: "你已登录",
		memberDesc: "邀请奖励只适用于新注册用户，去探索作品吧。",
		invalidTitle: "邀请码无效",
		invalidDesc: "链接可能已过期或不完整，你仍然可以直接加入 Muhaha。",
		browseCta: "去逛逛",
	},
	report: {
		title: "举报",
		description: "请选择举报原因，我们会尽快处理。",
		submitted: "举报已提交",
		submitFailed: "举报提交失败",
		type: {
			spam: "垃圾信息",
			nuditySexualContent: "色情内容",
			violentSpeech: "暴力言论",
			suicideSelfHarm: "自杀或自残",
			childSafety: "未成年人安全",
			illegalRegulatedBehaviors: "违法违规行为",
			misinformation: "虚假信息",
			terrorismHateHarassment: "恐怖主义、仇恨与骚扰",
		},
	},
};
